import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../lib/api";
import { Markdown } from "../components/Markdown";
import { Timeline } from "../components/Timeline";
import { StateBadge, PriorityBadge } from "../components/StateBadge";
import { DecisionDialog } from "../components/DecisionDialog";
import { RejectHoldDialog } from "../components/RejectHoldDialog";
import { CloseResolvedDialog } from "../components/CloseResolvedDialog";
import { Button } from "../components/ui/button";
import { fmtDate, fmtUSD, fmtECU, fmtECUWithUSD } from "../lib/format";
import { useBilling } from "../context/BillingContext";
import {
  CheckCircle2, XCircle, MessageSquareDiff, Pause, Play, ExternalLink,
  ArrowRight, Zap, ChevronDown, ChevronUp, CheckCheck,
} from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";

export default function TaskDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["task", id],
    queryFn: async () => (await api.get(`/tasks/${id}`)).data,
    refetchInterval: 10000,
  });

  const [open, setOpen] = useState(false);
  const [decision, setDecision] = useState("approve");
  const [expanded, setExpanded] = useState(false);
  const [rhOpen, setRhOpen] = useState(false);
  const [rhAction, setRhAction] = useState("hold");
  const [closeOpen, setCloseOpen] = useState(false);
  const { usdPerEcu } = useBilling();

  if (isLoading || !data) return <div className="p-6 text-muted-foreground">Loading…</div>;

  const { task, project, events } = data;
  const TERMINAL = ["done", "rejected"];
  const canRequestPlan = task.state === "draft";
  const canDecide = task.state === "plan_ready";
  // Hold is available on every holdable state (draft + active states). Reject
  // + Close-as-resolved are available on every non-terminal state (incl. on_hold).
  const canHold = ["draft","plan_requested","plan_ready","approved","building","emergent_testing","acceptance_testing","fix_requested","changes_requested"].includes(task.state);
  const canReject = !TERMINAL.includes(task.state);
  const canClose = !TERMINAL.includes(task.state);
  const canResume = task.state === "on_hold";

  const requestPlan = async () => {
    try {
      await api.post(`/tasks/${id}/request-plan`);
      toast.success("Plan requested from agent.");
      qc.invalidateQueries({ queryKey: ["task", id] });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openRh = (action) => { setRhAction(action); setRhOpen(true); };
  const resume = async () => {
    try {
      await api.post(`/tasks/${id}/resume`);
      toast.success("Task resumed.");
      qc.invalidateQueries({ queryKey: ["task", id] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const trigger = (d) => { setDecision(d); setOpen(true); };
  const onDone = () => qc.invalidateQueries({ queryKey: ["task", id] });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 pb-32 md:pb-8">
      {/* Header */}
      <div>
        {project && (
          <Link to={`/projects/${project.slug}`} className="text-xs font-mono text-primary hover:underline">
            {project.name}
          </Link>
        )}
        <div className="flex items-start justify-between gap-4 flex-wrap mt-1">
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="task-title">
            {task.title}
          </h1>
          <div className="flex items-center gap-2 flex-wrap">
            <Select
              value={task.priority}
              onValueChange={async (v) => {
                try {
                  await api.patch(`/tasks/${id}`, { priority: v });
                  toast.success(`Priority set to ${v}.`);
                  qc.invalidateQueries({ queryKey: ["task", id] });
                } catch (e) { toast.error(formatApiError(e)); }
              }}
            >
              <SelectTrigger className="h-7 w-28 text-xs font-mono uppercase" data-testid="priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
            <StateBadge state={task.state} />
          </div>
        </div>
        <div className="text-xs font-mono text-muted-foreground mt-2">
          #{task.id.slice(-6)} · {task.type} · {task.test_scope} tests · updated {fmtDate(task.updated_at)}
        </div>
      </div>

      {/* Description */}
      {task.description && (
        <div className="border border-border rounded-xl bg-card p-5">
          <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Description</p>
          <p className="text-sm whitespace-pre-wrap">{task.description}</p>
        </div>
      )}

      {/* CEO actions */}
      <div className="flex gap-2 flex-wrap">
        {canRequestPlan && (
          <Button onClick={requestPlan} data-testid="request-plan-btn">
            <Zap className="h-4 w-4 mr-2" /> Request plan
          </Button>
        )}
        {canHold && (
          <Button variant="outline" onClick={() => openRh("hold")} data-testid="hold-btn">
            <Pause className="h-4 w-4 mr-2" /> Hold
          </Button>
        )}
        {canReject && (
          <Button
            variant="outline"
            onClick={() => openRh("reject")}
            data-testid="reject-btn-detail"
            className="bg-rose-500/10 hover:bg-rose-500/20 text-rose-600 dark:text-rose-400 border border-rose-500/30"
          >
            <XCircle className="h-4 w-4 mr-2" /> Reject
          </Button>
        )}
        {canResume && (
          <Button variant="outline" onClick={resume} data-testid="resume-btn">
            <Play className="h-4 w-4 mr-2" /> Resume
          </Button>
        )}
        {canClose && (
          <Button
            variant="outline"
            onClick={() => setCloseOpen(true)}
            data-testid="close-resolved-btn"
            className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30"
          >
            <CheckCheck className="h-4 w-4 mr-2" /> Close as resolved
          </Button>
        )}
      </div>

      {/* Resolution note (closed-as-resolved) */}
      {task.state === "done" && task.resolution_note && (
        <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-xl p-4" data-testid="resolution-note-card">
          <p className="text-[11px] font-mono uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mb-1">Closed as resolved</p>
          <p className="text-sm">{task.resolution_note}</p>
          {task.resolved_at && <p className="text-xs font-mono text-muted-foreground mt-1">{fmtDate(task.resolved_at)}{task.resolved_by ? ` · ${task.resolved_by}` : ""}</p>}
        </div>
      )}

      {/* Hold / reject reasons */}
      {task.state === "on_hold" && task.hold_reason && (
        <div className="border border-amber-500/30 bg-amber-500/5 rounded-xl p-4" data-testid="hold-reason-card">
          <p className="text-[11px] font-mono uppercase tracking-widest text-amber-600 dark:text-amber-400 mb-1">On hold</p>
          <p className="text-sm">{task.hold_reason}</p>
        </div>
      )}
      {task.state === "rejected" && (task.rejected_reason || task.rejected_at) && (
        <div className="border border-rose-500/30 bg-rose-500/5 rounded-xl p-4" data-testid="reject-reason-card">
          <p className="text-[11px] font-mono uppercase tracking-widest text-rose-600 dark:text-rose-400 mb-1">Rejected</p>
          {task.rejected_reason ? <p className="text-sm">{task.rejected_reason}</p> : <p className="text-sm text-muted-foreground">No reason provided.</p>}
          {task.rejected_at && <p className="text-xs font-mono text-muted-foreground mt-1">{fmtDate(task.rejected_at)}{task.rejected_by ? ` · ${task.rejected_by}` : ""}</p>}
        </div>
      )}

      {/* Plan */}
      {task.plan && (
        <div className="border border-border rounded-xl bg-card p-5" data-testid="plan-card">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Plan by {task.assigned_agent || "agent"}</p>
            <div className="flex items-center gap-4 text-xs font-mono">
              <span style={{color: "hsl(var(--usd))"}}>{fmtUSD(task.plan.estimate_usd)}</span>
              <span style={{color: "hsl(var(--ecu))"}}>{fmtECUWithUSD(task.plan.estimate_ecu, usdPerEcu)}</span>
            </div>
          </div>
          <h3 className="font-display text-lg font-semibold mt-2" data-testid="plan-summary">
            {task.plan.summary || "(no summary)"}
          </h3>
          {task.plan.risks?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {task.plan.risks.map((r, i) => (
                <span key={i} className="text-[11px] px-2 py-0.5 rounded-full border border-amber-500/30 text-amber-500 bg-amber-500/10">
                  ⚠ {r}
                </span>
              ))}
            </div>
          )}
          <button
            onClick={() => setExpanded((v) => !v)}
            className="mt-4 text-xs font-mono text-muted-foreground hover:text-foreground flex items-center gap-1"
            data-testid="plan-toggle"
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {expanded ? "Hide" : "View"} full plan
          </button>
          {expanded && (
            <div className="mt-3 border-t border-border pt-4">
              <Markdown>{task.plan.text}</Markdown>
            </div>
          )}
        </div>
      )}

      {/* CEO decision */}
      {task.ceo_decision && (
        <div className="border border-border rounded-xl bg-card p-5">
          <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">CEO decision</p>
          <p className="text-sm mt-1">
            <span className="font-semibold">{task.ceo_decision.decision}</span>
            {task.ceo_decision.note && <> — {task.ceo_decision.note}</>}
          </p>
          <p className="text-xs font-mono text-muted-foreground mt-1">{fmtDate(task.ceo_decision.decided_at)}</p>
        </div>
      )}

      {/* Emergent summary */}
      {task.emergent_summary && (
        <div className="border border-border rounded-xl bg-card p-5">
          <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Emergent summary</p>
          <Markdown>{task.emergent_summary}</Markdown>
        </div>
      )}

      {/* Acceptance */}
      {task.acceptance && (
        <div className="border border-border rounded-xl bg-card p-5" data-testid="acceptance-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Acceptance tests</p>
            <p className="font-mono text-sm">
              <span className="text-emerald-500 font-semibold">{task.acceptance.passed}</span>
              /{task.acceptance.total} passed
            </p>
          </div>
          <ul className="mt-3 space-y-1">
            {task.acceptance.results.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                {r.passed ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" />
                ) : (
                  <XCircle className="h-4 w-4 text-rose-500 mt-0.5" />
                )}
                <div>
                  <p className="font-medium">{r.name}</p>
                  {r.detail && <p className="text-xs text-muted-foreground font-mono">{r.detail}</p>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Preview link */}
      {task.preview_url && (
        <a
          href={task.preview_url} target="_blank" rel="noopener noreferrer"
          data-testid="preview-link"
          className="inline-flex items-center gap-2 text-primary text-sm hover:underline"
        >
          Open preview <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}

      {/* Timeline */}
      <div className="border border-border rounded-xl bg-card p-5">
        <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Timeline</p>
        <Timeline events={events} />
      </div>

      {/* Sticky mobile approval bar */}
      {canDecide && (
        <div className="fixed bottom-0 left-0 right-0 z-40 md:static md:z-auto md:bg-transparent md:border-0 md:p-0 p-3 bg-background/95 backdrop-blur-md border-t border-border flex gap-2">
          <Button
            onClick={() => trigger("approve")}
            data-testid="approve-btn-main"
            className="flex-1 h-14 md:h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base rounded-xl shadow-lg shadow-emerald-600/20"
          >
            <CheckCircle2 className="h-5 w-5 mr-2" />Approve
          </Button>
          <Button
            onClick={() => trigger("changes")}
            data-testid="changes-btn-main"
            variant="outline"
            className="flex-1 h-14 md:h-11 bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/30 font-bold text-base rounded-xl"
          >
            <MessageSquareDiff className="h-5 w-5 mr-2" />Changes
          </Button>
          <Button
            onClick={() => trigger("reject")}
            data-testid="reject-btn-main"
            className="flex-1 h-14 md:h-11 bg-rose-600 hover:bg-rose-700 text-white font-bold text-base rounded-xl shadow-lg shadow-rose-600/20"
          >
            <XCircle className="h-5 w-5 mr-2" />Reject
          </Button>
        </div>
      )}

      <DecisionDialog
        open={open}
        onOpenChange={setOpen}
        taskId={id}
        decision={decision}
        onDone={onDone}
      />
      <RejectHoldDialog
        open={rhOpen}
        onOpenChange={setRhOpen}
        taskId={id}
        action={rhAction}
        onDone={onDone}
      />
      <CloseResolvedDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        taskId={id}
        onDone={onDone}
      />
    </div>
  );
}
